import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { IonicModule, AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { addIcons } from 'ionicons';
import { informationCircle, logOutOutline, downloadOutline } from 'ionicons/icons';

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule]
})
export class AppHeaderComponent implements OnInit {
  @Input() title: string = '';
  @Output() help = new EventEmitter<void>();
  @Output() signOut = new EventEmitter<void>();
  
  showInstallButton = false;
  private deferredPrompt: any;

  constructor(
    private router: Router,
    private alertController: AlertController
  ) {
    addIcons({ informationCircle, logOutOutline, downloadOutline });
  }

  ngOnInit() {
    this.initializePWAInstall();
  }

  private initializePWAInstall() {
    // Check if already running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      this.showInstallButton = false;
      return;
    }
    
    const isInstalled = localStorage.getItem('pwa-installed') === 'true';
    if (isInstalled) {
      this.showInstallButton = false;
      return;
    }

    // Always show install button (works for both Chrome and Safari)
    this.showInstallButton = true;

    // Listen for Chrome/Edge install prompt
    window.addEventListener('beforeinstallprompt', (e: any) => {
      e.preventDefault();
      this.deferredPrompt = e;
    });

    window.addEventListener('appinstalled', () => {
      this.showInstallButton = false;
      localStorage.setItem('pwa-installed', 'true');
      this.deferredPrompt = null;
    });
  }

  async installPWA() {
    // Chrome/Edge: Use native install prompt
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
        this.showInstallButton = false;
      }

      this.deferredPrompt = null;
      return;
    }

    // Safari/iOS: Show install instructions using Ionic alert
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSChrome = isIOS && /CriOS/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && !isIOSChrome;
    
    let header = 'Install App for Easy Access';
    let message = '';
    
    if (isIOS) {
      if (isIOSChrome) {
        // iOS Chrome - must use Safari to install
        message = '1. Open this page in Safari browser<br>2. Tap the Share button at the bottom (⬆️)<br>3. Scroll and tap "Add to Home Screen"<br><br><small>Note: iOS requires Safari for app installation.</small>';
      } else {
        // iOS Safari
        message = '1. Tap the Share button at the bottom (square with arrow ⬆️)<br>2. Scroll down and tap "Add to Home Screen"<br>3. Tap "Add" to confirm';
      }
    } else if (isAndroid && isChrome) {
      // Android Chrome - manual instructions
      message = '1. Tap the menu (⋮) in the top-right corner<br>2. Select "Add to Home screen" or "Install app"<br>3. Tap "Add" or "Install" to confirm<br><br><small>Note: If you don\'t see the install option, the app may already be installed or your browser needs to be updated.</small>';
    } else if (isSafari) {
      // macOS Safari
      message = '1. Click the Share button in Safari<br>2. Select "Add to Dock"';
    } else {
      message = 'Install is not available. Please use Chrome, Edge, or Safari browser to install this app.';
    }

    const alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: ['OK']
    });

    await alert.present();
  }

  onHelp() {
    this.help.emit();
  }

  async onSignOut() {
    try {
      const response = await fetch(`${environment.baseUrl}/api/logout`, {
        method: 'POST',
        credentials: 'include' // Include session cookie
      });
      
      if (response.ok) {
        // Redirect to welcome page after successful logout
        this.router.navigate(['/welcome']);
      } else {
        console.error('Logout failed');
        // Still redirect to welcome even if logout fails
        this.router.navigate(['/welcome']);
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect to welcome even on error
      this.router.navigate(['/welcome']);
    }
  }
}
